import type { IssueNoPointReason } from '@cortex/domain-ac-mens-hub-api';
import { Badge } from '@cortex/web-design-system/components/ui/Badge';
import { Button } from '@cortex/web-design-system/components/ui/Button';
import { ExternalLink } from 'lucide-react';
import { type ReactNode, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { useIngestWrite } from '../../hooks/useIssueIngest.js';
import { useExcludedVoices } from '../../hooks/useIssueTodo.js';
import { formatIssueTime } from '../../lib/issue-mgmt-format.js';
import { IssueText } from './IssueText.js';
import { useIssueToast } from './useIssueToast.js';

/**
 * 除外の一覧を一度に増やす件数と上限（API の上限と同じ）。
 * @graph-connects none
 */
const EXCLUDED_PAGE_SIZE = 20;
/** @graph-connects none */
const EXCLUDED_LIMIT_MAX = 200;

/**
 * AI が除外した理由の札の文言キー。
 * @graph-connects none
 */
export const NO_POINT_REASON_LABEL_KEYS: Record<IssueNoPointReason, string> = {
  greeting: 'issueMgmt.excluded.reason.greeting',
  announcement: 'issueMgmt.excluded.reason.announcement',
  scheduling: 'issueMgmt.excluded.reason.scheduling',
  answer_or_report: 'issueMgmt.excluded.reason.answerOrReport',
  test_or_noise: 'issueMgmt.excluded.reason.testOrNoise',
  other: 'issueMgmt.excluded.reason.other',
};

/**
 * 除外・点検の1行（投稿日時とチャンネル・本文・札・操作）。
 * @graph-stack ac-mens-hub
 * @graph-domain Operations
 * @graph-business AI が除外した投稿や点検で抜き取った声を、投稿日時・チャンネル・本文・札・操作つきで1行に出す
 * @graph-connects formatIssueTime [calls] 投稿日時
 * @graph-connects IssueText [calls] 伏せ字の札
 */
export function VoiceRow({
  postedAt,
  postedDate,
  channelName,
  isPrivate,
  text,
  slackUrl,
  badge,
  action,
  now,
}: {
  postedAt: string | null;
  postedDate: string | null;
  channelName: string | null;
  isPrivate: boolean;
  text: ReactNode;
  slackUrl: string | null;
  badge: ReactNode;
  action: ReactNode;
  now: Date;
}) {
  const { t } = useTranslation();
  return (
    <li className="flex flex-wrap items-center gap-x-4 gap-y-1 px-4 py-2.5 text-sm">
      <span className="w-40 shrink-0 text-xs text-[var(--color-text-muted)]">
        {formatIssueTime(postedAt, postedDate, now) ?? t('issueMgmt.common.dash')}{' '}
        {channelName === null ? '' : `#${channelName}`}
        {isPrivate && (
          <span className="ml-1" title={t('issueMgmt.common.privateHint')}>
            <Badge size="sm">{t('issueMgmt.common.private')}</Badge>
          </span>
        )}
      </span>
      <span className="min-w-[12rem] flex-1 text-[var(--color-text-primary)]">{text}</span>
      {badge}
      {slackUrl !== null && (
        <a
          href={slackUrl}
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center gap-1 text-xs text-[var(--color-accent-primary)] hover:underline"
        >
          {t('issueMgmt.common.openSlack')}
          <ExternalLink className="h-3 w-3" aria-hidden="true" />
        </a>
      )}
      {action}
    </li>
  );
}

/**
 * やることの最下段の「AIが除外した投稿（N件）を見る」と、開いたときの除外の一覧（SC-01b）。
 * 直近30日の除外を新しい順に出し、行の「振り分けに戻す」で本文をそのまま要点にした振り分けのカードを1枚増やす。
 *
 * @graph-node {Function}
 * @graph-stack ac-mens-hub
 * @graph-domain Operations
 * @graph-business AI が論点なしとして除外した直近30日の投稿を見て、課題が混じっていれば1操作で振り分けに戻す
 * @graph-connects useExcludedVoices [calls] 除外の一覧
 * @graph-connects useIngestWrite [calls] restoreExcludedVoice
 * @graph-connects VoiceRow [calls] 1行
 */
export function ExcludedPanel({
  open,
  onToggle,
  now,
}: {
  open: boolean;
  onToggle: () => void;
  now: Date;
}) {
  const { t } = useTranslation();
  const toast = useIssueToast();
  const [limit, setLimit] = useState(EXCLUDED_PAGE_SIZE);
  const excluded = useExcludedVoices(limit, true);
  const restore = useIngestWrite('restoreExcludedVoice');
  const data = excluded.data;
  const total = data?.total ?? null;

  return (
    <section className="mt-6">
      <button
        type="button"
        onClick={onToggle}
        className="text-sm text-[var(--color-accent-primary)] hover:underline"
      >
        {open
          ? t('issueMgmt.excluded.close')
          : total === null
            ? t('issueMgmt.excluded.openNoCount')
            : t('issueMgmt.excluded.open', { n: String(total) })}
      </button>
      {open && data !== undefined && (
        <div className="mt-2 rounded-xl border border-[var(--color-border)] bg-[var(--color-bg-surface)]">
          {data.items.length === 0 ? (
            <p className="px-4 py-3 text-sm text-[var(--color-text-muted)]">
              {t('issueMgmt.excluded.empty')}
            </p>
          ) : (
            <ul className="divide-y divide-[var(--color-border)]">
              {data.items.map((voice) => (
                <VoiceRow
                  key={voice.voiceId}
                  postedAt={voice.postedAt}
                  postedDate={voice.postedDate}
                  channelName={voice.channelName}
                  isPrivate={voice.isPrivate}
                  text={<IssueText text={voice.text} />}
                  slackUrl={voice.slackUrl}
                  badge={
                    <Badge>
                      {voice.reason === null
                        ? t('issueMgmt.excluded.reason.none')
                        : t(NO_POINT_REASON_LABEL_KEYS[voice.reason])}
                    </Badge>
                  }
                  action={
                    <Button
                      variant="secondary"
                      size="sm"
                      disabled={restore.isPending}
                      onClick={() => {
                        restore.mutate(
                          { voiceId: voice.voiceId },
                          {
                            onSuccess: () => {
                              toast.notifySaved(t('issueMgmt.excluded.restored'), {
                                detail: t('issueMgmt.excluded.restoredDetail'),
                              });
                            },
                            onError: toast.notifyError,
                          },
                        );
                      }}
                    >
                      {t('issueMgmt.excluded.restore')}
                    </Button>
                  }
                  now={now}
                />
              ))}
            </ul>
          )}
          {data.items.length < data.total && limit < EXCLUDED_LIMIT_MAX && (
            <button
              type="button"
              onClick={() => {
                setLimit((n) => Math.min(n + EXCLUDED_PAGE_SIZE, EXCLUDED_LIMIT_MAX));
              }}
              className="w-full border-t border-[var(--color-border)] px-4 py-2.5 text-left text-xs text-[var(--color-text-muted)] hover:bg-[var(--color-bg-hover)]"
            >
              {t('issueMgmt.excluded.more', { n: String(data.total - data.items.length) })}
            </button>
          )}
        </div>
      )}
    </section>
  );
}
